import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  Consent,
  ConsentType,
  Experience,
  ExperienceType,
  Interest,
  Need,
  NeedStatus,
  NeedType,
  OrganizationProfile,
  Profile,
  ProfileAccountType,
  ProfileStatusHistoryEntry,
  ProfileVisibility,
  Reference,
  ReferenceType,
  Skill,
  SkillLevel,
  TpmeBusinessMaturity,
  TpmeFinancialNeed,
  TpmeProductService,
  YoungProfile,
} from '../models';

/** Réponse de GET /profiles/me/completion (profile-service, via gateway). */
export interface ProfileCompletion {
  score: number;
  missingFields: string[];
  nextAction: string;
}

/**
 * Réponse de GET /profiles/me/organization/completion (OrganizationCompletionResponse, UC-31/UC-32).
 * Contrôle strictement plus de champs que la complétude générique (voir
 * ProfileService#organizationMissingFields côté backend) : nom/type/secteur/description
 * d'organisation, représentant légal, et numéro d'enregistrement/forme juridique si `formalized`.
 * `canSubmit` est la seule source fiable pour activer le bouton de soumission — un statut
 * DRAFT/NEEDS_CORRECTION ne suffit pas, il faut aussi qu'aucun champ obligatoire ne manque.
 */
export interface OrganizationCompletion {
  score: number;
  missingFields: string[];
  canSubmit: boolean;
  nextAction: string;
}

/** Payload de PUT /profiles/me : profil racine (ProfileUpsertRequest). */
export interface ProfileUpsertPayload {
  accountType: ProfileAccountType;
  displayName: string;
  headline?: string | null;
  bio?: string | null;
  preferredLanguage?: string | null;
}

/** Payload de PATCH /profiles/me/contact (ProfileContactUpdateRequest). */
export interface ProfileContactPayload {
  primaryEmail: string | null;
  primaryPhone: string | null;
}

/** Payload de PATCH /profiles/me/location (ProfileLocationUpdateRequest). */
export interface ProfileLocationPayload {
  countryCode?: string | null;
  regionCode: string | null;
  departmentCode?: string | null;
  city: string | null;
  address?: string | null;
}

/** Payload de PUT /profiles/me/organization (OrganizationProfileUpsertRequest) : remplace toute la ligne. */
export interface OrganizationProfilePayload {
  legalName: string | null;
  tradeName: string | null;
  organizationType: string | null;
  registrationNumber: string | null;
  taxIdentifier: string | null;
  creationYear: number | null;
  sectorCode: string | null;
  website: string | null;
  description: string | null;
  employeeCountRange: string | null;
  annualRevenueRange: string | null;
  targetMarkets: string | null;
  formalized: boolean;
  legalForm: string | null;
  representativeName: string | null;
  representativePosition: string | null;
}

/** Réponse générique de profile-service (ex. POST /profiles/me/submit). */
export interface ApiMessageResponse {
  message: string;
}

/** Payload de POST/PUT /profiles/me/tpme/products-services (TpmeProductServiceRequest). */
export interface TpmeProductServicePayload {
  name: string;
  type: string;
  description?: string | null;
  sectorCode?: string | null;
  marketStage?: string | null;
  targetCustomers?: string | null;
  unitPrice?: number | null;
  currencyCode?: string | null;
  active?: boolean;
}

/** Payload de PUT /profiles/me/tpme/business-maturity (TpmeBusinessMaturityRequest) : overallScore est calculé côté serveur, jamais envoyé. */
export interface TpmeBusinessMaturityPayload {
  businessPlanScore: number;
  accountingScore: number;
  marketValidationScore: number;
  teamScore: number;
  legalComplianceScore: number;
  digitalReadinessScore: number;
  strengths?: string | null;
  improvementAreas?: string | null;
}

/** Payload de POST/PUT /profiles/me/tpme/financial-needs (TpmeFinancialNeedRequest, UC-35). */
export interface TpmeFinancialNeedPayload {
  title: string;
  financingType: string;
  amount: number;
  currencyCode?: string | null;
  purpose?: string | null;
  repaymentCapacityRange?: string | null;
  urgency?: string | null;
  preferredTenor?: string | null;
  collateralDescription?: string | null;
  status?: string | null;
  expectedDecisionDate?: string | null;
}

/** Payload de POST/PATCH /profiles/me/skills (ProfileSkillCreateRequest / ProfileSkillUpdateRequest). */
export interface ProfileSkillPayload {
  skillCode: string;
  skillName: string;
  level: SkillLevel;
  yearsOfExperience: number | null;
}

/** Payload de POST /profiles/me/interests (ProfileInterestCreateRequest). */
export interface ProfileInterestPayload {
  interestCode: string;
  interestName: string;
}

/** Payload de POST /profiles/me/needs (ProfileNeedCreateRequest). */
export interface ProfileNeedCreatePayload {
  needType: NeedType;
  title: string;
  description: string | null;
  priority: number;
}

/** Payload de PATCH /profiles/me/needs/{id} (ProfileNeedUpdateRequest). */
export interface ProfileNeedUpdatePayload {
  title?: string;
  description?: string | null;
  priority?: number;
  status?: NeedStatus;
}

/** Payload de PATCH /profiles/me/skills/{id} (ProfileSkillUpdateRequest) : tous les champs optionnels. */
export interface ProfileSkillUpdatePayload {
  skillName?: string;
  level?: SkillLevel;
  yearsOfExperience?: number | null;
}

/** Payload de POST /profiles/me/experiences (ProfileExperienceCreateRequest). */
export interface ProfileExperienceCreatePayload {
  experienceType: ExperienceType;
  title: string;
  organization: string | null;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  current: boolean;
}

/** Payload de PATCH /profiles/me/experiences/{id} (ProfileExperienceUpdateRequest) : tous les champs optionnels. */
export type ProfileExperienceUpdatePayload = Partial<ProfileExperienceCreatePayload>;

/**
 * Payload de POST /profiles/me/references (ProfileReferenceCreateRequest). externalService/externalId
 * n'ont de sens qu'une fois document-storage-service/media-library-service branchés à cet écran ;
 * en attendant, on n'expose que le cas MANUAL (lien externe saisi à la main).
 */
export interface ProfileReferenceCreatePayload {
  referenceType: ReferenceType;
  externalService: string;
  externalId: string;
  label: string;
  url: string | null;
}

/** Payload de POST /profiles/me/avatar (ProfileAvatarRequest) : externalId = StoredDocument.id d'un fichier déjà envoyé à document-storage-service (documentTypeCode PROFILE_PHOTO). */
export interface ProfileAvatarPayload {
  externalId: string;
  url?: string | null;
  label?: string | null;
}

/** Payload de PUT /profiles/me/consents (ProfileConsentUpdateRequest). */
export interface ProfileConsentUpdatePayload {
  consents: { consentType: ConsentType; granted: boolean; source?: string }[];
}

/**
 * Payload de PATCH /profiles/me/visibility (ProfileVisibilityUpdateRequest). Distinct des
 * consentements : granter le consentement PUBLIC_VISIBILITY (ProfileConsent) ne modifie jamais ce
 * champ côté backend, aucun lien entre les deux n'existe — c'est le seul endpoint qui fait
 * réellement apparaître le profil dans /profiles/search (avec status=VALIDATED).
 */
export interface ProfileVisibilityPayload {
  visibility: ProfileVisibility;
}

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly http = inject(HttpClient);
  // profile-service est joint via le gateway sous ce préfixe (StripPrefix=1 côté gateway).
  private readonly baseUrl = `${environment.apiUrl}/profile-service/api/v1/profiles/me`;

  // ---- Profil principal (UC-07) ----

  getProfile(): Observable<Profile> {
    return this.http.get<Profile>(this.baseUrl);
  }

  /** Crée ou met à jour la racine du profil (type de compte, nom affiché, accroche, bio, langue). */
  updateProfile(payload: ProfileUpsertPayload): Observable<Profile> {
    return this.http.put<Profile>(this.baseUrl, payload);
  }

  updateContact(payload: ProfileContactPayload): Observable<Profile> {
    return this.http.patch<Profile>(`${this.baseUrl}/contact`, payload);
  }

  updateLocation(payload: ProfileLocationPayload): Observable<Profile> {
    return this.http.patch<Profile>(`${this.baseUrl}/location`, payload);
  }

  /** Seul endpoint qui rend le profil découvrable dans l'annuaire /profiles/search (voir ProfileVisibilityPayload). */
  updateVisibility(payload: ProfileVisibilityPayload): Observable<Profile> {
    return this.http.patch<Profile>(`${this.baseUrl}/visibility`, payload);
  }

  // ---- Organisation partenaire (UC-25) ----

  /** PUT /organization remplace toute la ligne OrganizationProfile : envoyer systématiquement l'objet complet. */
  updateOrganization(payload: OrganizationProfilePayload): Observable<OrganizationProfile> {
    return this.http.put<OrganizationProfile>(`${this.baseUrl}/organization`, payload);
  }

  /** Passe le profil de DRAFT/NEEDS_CORRECTION vers SUBMITTED_FOR_REVIEW si les champs requis sont présents. */
  submitProfile(): Observable<ApiMessageResponse> {
    return this.http.post<ApiMessageResponse>(`${this.baseUrl}/submit`, {});
  }

  /**
   * Complétude de la fiche organisation (UC-31/UC-32) : à utiliser à la place de getCompletion()
   * pour un compte PARTNER/STARTUP/TPME, car elle contrôle aussi les champs organisationnels.
   */
  getOrganizationCompletion(): Observable<OrganizationCompletion> {
    return this.http.get<OrganizationCompletion>(`${this.baseUrl}/organization/completion`);
  }

  /**
   * Soumet la fiche organisation pour validation (UC-31/UC-32). Réservée aux accountType
   * PARTNER/STARTUP/TPME ; distincte de submitProfile() car elle valide aussi les champs
   * organisationnels (voir ProfileService#organizationMissingFields côté backend).
   */
  submitOrganization(): Observable<ApiMessageResponse> {
    return this.http.post<ApiMessageResponse>(`${this.baseUrl}/organization/submit`, {});
  }

  /** Historique des changements de statut du profil (UC-32) — validation, correction, suspension, archivage. */
  getTimeline(): Observable<ProfileStatusHistoryEntry[]> {
    return this.http.get<ProfileStatusHistoryEntry[]>(`${this.baseUrl}/timeline`);
  }

  /** PUT /young remplace toute la ligne YoungProfile : envoyer systématiquement l'objet complet. */
  updateYoungProfile(payload: YoungProfile): Observable<YoungProfile> {
    return this.http.put<YoungProfile>(`${this.baseUrl}/young`, payload);
  }

  /** Score de complétude, champs manquants et prochaine action conseillée pour le profil connecté. */
  getCompletion(): Observable<ProfileCompletion> {
    return this.http.get<ProfileCompletion>(`${this.baseUrl}/completion`);
  }

  // ---- Compétences (UC-08) ----

  getSkills(): Observable<Skill[]> {
    return this.http.get<Skill[]>(`${this.baseUrl}/skills`);
  }

  addSkill(payload: ProfileSkillPayload): Observable<Skill> {
    return this.http.post<Skill>(`${this.baseUrl}/skills`, payload);
  }

  updateSkill(id: string, payload: ProfileSkillUpdatePayload): Observable<Skill> {
    return this.http.patch<Skill>(`${this.baseUrl}/skills/${id}`, payload);
  }

  removeSkill(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/skills/${id}`);
  }

  // ---- Centres d'intérêt (UC-08) ----

  getInterests(): Observable<Interest[]> {
    return this.http.get<Interest[]>(`${this.baseUrl}/interests`);
  }

  addInterest(payload: ProfileInterestPayload): Observable<Interest> {
    return this.http.post<Interest>(`${this.baseUrl}/interests`, payload);
  }

  removeInterest(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/interests/${id}`);
  }

  // ---- Besoins (UC-08) ----

  getNeeds(): Observable<Need[]> {
    return this.http.get<Need[]>(`${this.baseUrl}/needs`);
  }

  addNeed(payload: ProfileNeedCreatePayload): Observable<Need> {
    return this.http.post<Need>(`${this.baseUrl}/needs`, payload);
  }

  updateNeed(id: string, payload: ProfileNeedUpdatePayload): Observable<Need> {
    return this.http.patch<Need>(`${this.baseUrl}/needs/${id}`, payload);
  }

  removeNeed(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/needs/${id}`);
  }

  // ---- Expériences (UC-08) ----

  getExperiences(): Observable<Experience[]> {
    return this.http.get<Experience[]>(`${this.baseUrl}/experiences`);
  }

  addExperience(payload: ProfileExperienceCreatePayload): Observable<Experience> {
    return this.http.post<Experience>(`${this.baseUrl}/experiences`, payload);
  }

  updateExperience(id: string, payload: ProfileExperienceUpdatePayload): Observable<Experience> {
    return this.http.patch<Experience>(`${this.baseUrl}/experiences/${id}`, payload);
  }

  removeExperience(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/experiences/${id}`);
  }

  // ---- Références (UC-08) ----

  getReferences(): Observable<Reference[]> {
    return this.http.get<Reference[]>(`${this.baseUrl}/references`);
  }

  addReference(payload: ProfileReferenceCreatePayload): Observable<Reference> {
    return this.http.post<Reference>(`${this.baseUrl}/references`, payload);
  }

  removeReference(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/references/${id}`);
  }

  /** Rattache la photo de profil (référence PHOTO) une fois le fichier envoyé à document-storage-service. */
  updateAvatar(payload: ProfileAvatarPayload): Observable<Reference> {
    return this.http.post<Reference>(`${this.baseUrl}/avatar`, payload);
  }

  // ---- Consentements (UC-08) ----

  getConsents(): Observable<Consent[]> {
    return this.http.get<Consent[]>(`${this.baseUrl}/consents`);
  }

  updateConsents(payload: ProfileConsentUpdatePayload): Observable<Consent[]> {
    return this.http.put<Consent[]>(`${this.baseUrl}/consents`, payload);
  }

  // ---- Catalogue produits/services TPME (UC-29/UC-30) ----

  getTpmeProductServices(): Observable<TpmeProductService[]> {
    return this.http.get<TpmeProductService[]>(`${this.baseUrl}/tpme/products-services`);
  }

  addTpmeProductService(payload: TpmeProductServicePayload): Observable<TpmeProductService> {
    return this.http.post<TpmeProductService>(`${this.baseUrl}/tpme/products-services`, payload);
  }

  updateTpmeProductService(id: string, payload: TpmeProductServicePayload): Observable<TpmeProductService> {
    return this.http.put<TpmeProductService>(`${this.baseUrl}/tpme/products-services/${id}`, payload);
  }

  removeTpmeProductService(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/tpme/products-services/${id}`);
  }

  // ---- Maturité business TPME (UC-29) ----

  /** Ne 404 jamais : renvoie un objet à zéro (id null) si aucun diagnostic n'a encore été saisi. */
  getTpmeBusinessMaturity(): Observable<TpmeBusinessMaturity> {
    return this.http.get<TpmeBusinessMaturity>(`${this.baseUrl}/tpme/business-maturity`);
  }

  updateTpmeBusinessMaturity(payload: TpmeBusinessMaturityPayload): Observable<TpmeBusinessMaturity> {
    return this.http.put<TpmeBusinessMaturity>(`${this.baseUrl}/tpme/business-maturity`, payload);
  }

  // ---- Besoins de financement TPME (UC-35) ----

  getTpmeFinancialNeeds(): Observable<TpmeFinancialNeed[]> {
    return this.http.get<TpmeFinancialNeed[]>(`${this.baseUrl}/tpme/financial-needs`);
  }

  addTpmeFinancialNeed(payload: TpmeFinancialNeedPayload): Observable<TpmeFinancialNeed> {
    return this.http.post<TpmeFinancialNeed>(`${this.baseUrl}/tpme/financial-needs`, payload);
  }

  updateTpmeFinancialNeed(id: string, payload: TpmeFinancialNeedPayload): Observable<TpmeFinancialNeed> {
    return this.http.put<TpmeFinancialNeed>(`${this.baseUrl}/tpme/financial-needs/${id}`, payload);
  }

  removeTpmeFinancialNeed(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/tpme/financial-needs/${id}`);
  }
}
